import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { hasBackLayer, requestLayerBack, navigateAfterLayers, routeHistoryState } from "./data/layers";
import type { AppState } from "../api";
import { defaultSection, resolveRoute, routeUrl, type Route, type SectionId } from "./navigation";

type Entry = { route: Route; depth: number; key: string; menu: boolean };
const stateKey = "estet-next-navigation";
const newKey = () => Math.random().toString(36).slice(2);
const routeKey = (route: Route) => `${route.section}/${route.tab || ""}/${route.bagId || ""}/${route.gallery ? "photo" : ""}/${route.objectId || ""}`;

export function useNavigation(user: AppState) {
  const [entry, setEntry] = useState<Entry>(() => ({ route: resolveRoute(location.search, user, location.hash), depth: 0, key: newKey(), menu: false }));
  const [layerVersion, setLayerVersion] = useState(0);
  useEffect(() => { const change = () => setLayerVersion(v => v + 1); window.addEventListener("estet:layers-changed", change); return () => window.removeEventListener("estet:layers-changed", change); }, []);
  const active = useRef(entry);
  const scroll = useRef(new Map<string, number>());
  const lastScroll = useRef(new Map<string, number>());
  const resetScrollOnPop = useRef(false);
  const lastRoutes = useRef(new Map<SectionId, Route>());
  active.current = entry;

  const goHome = () => navigateAfterLayers(() => {
    const current = active.current;
    const route = resolveRoute(`?section=${defaultSection(user)}`, user);
    const sameRoute = routeKey(current.route) === routeKey(route);
    if (sameRoute && current.menu) {
      resetScrollOnPop.current = true;
      history.back();
      return;
    }
    const key = newKey();
    scroll.current.set(key, 0);
    const depth = current.depth + (current.menu || sameRoute ? 0 : 1);
    commitEntry({ route, depth, key, menu: false }, current.menu || sameRoute);
  });
  useEffect(() => {
    // Start a bounded history for this authenticated session; Back never guesses
    // whether a preceding browser entry belongs to this app.
    history.replaceState({ ...history.state, [stateKey]: active.current }, "", routeUrl(active.current.route, location.href));
    const previousRestoration = history.scrollRestoration;
    history.scrollRestoration = "manual";
    const pop = () => {
      scroll.current.set(active.current.key, window.scrollY);
      lastScroll.current.set(routeKey(active.current.route), window.scrollY);
      const saved = history.state?.[stateKey] as Entry | undefined;
      const route = resolveRoute(location.search, user, location.hash);
      const next = saved ? { ...saved, route } : { route, depth: 0, key: newKey(), menu: false };
      if (resetScrollOnPop.current) {
        resetScrollOnPop.current = false;
        scroll.current.set(next.key, 0);
        window.scrollTo({ top: 0, behavior: "instant" });
      }
      active.current = next; setEntry(next);
    };
    window.addEventListener("popstate", pop);
    return () => {
      window.removeEventListener("popstate", pop);
      history.scrollRestoration = previousRestoration;
    };
  }, [user]);

  useLayoutEffect(() => {
    window.scrollTo({ top: scroll.current.get(entry.key) || 0, behavior: "instant" });
  }, [entry.key]);

  const commitEntry = (next: Entry, replace = false) => {
    scroll.current.set(active.current.key, window.scrollY);
    lastScroll.current.set(routeKey(active.current.route), window.scrollY);
    history[replace ? "replaceState" : "pushState"]({ ...routeHistoryState(history.state), [stateKey]: next }, "", routeUrl(next.route, location.href));
    active.current = next; setEntry(next);
  };
  const write = (next: Entry, replace = false) => navigateAfterLayers(() => commitEntry(next, replace));
  const openMenu = () => {
    if (!entry.menu) write({ ...entry, depth: entry.depth + 1, menu: true });
  };
  const back = () => { if (entry.depth > 0) history.back(); };
  const closeMenu = () => { if (entry.menu) back(); };
  const openBag = (bagId: string, photo = false) => navigateAfterLayers(() => {
    let current = active.current;
    if (current.route.bagId !== bagId) {
      commitEntry({ route: { ...current.route, bagId, gallery: undefined }, depth: current.depth + 1, key: newKey(), menu: false });
      current = active.current;
    }
    if (photo && !current.route.gallery) commitEntry({ ...current, route: { ...current.route, gallery: true }, depth: current.depth + 1, key: newKey() });
  });
  const closeLayer = () => {
    if (active.current.depth > 0) { history.back(); return; }
    const { bagId, gallery, ...list } = active.current.route;
    write({ ...active.current, route: gallery ? { ...list, bagId } : list, key: newKey(), menu: false }, true);
  };
  const go = (section: SectionId, tab?: string, objectId?: string) => {
    lastRoutes.current.set(entry.route.section, entry.route);
    const requested = { section, tab: tab ?? lastRoutes.current.get(section)?.tab, objectId };
    const next = resolveRoute(new URL(routeUrl(requested, location.href), location.origin).search, user);
    if (next.section === entry.route.section && next.tab === entry.route.tab && !entry.route.bagId && next.objectId === entry.route.objectId) {
      closeMenu();
      return;
    }
    const key = newKey();
    scroll.current.set(key, lastScroll.current.get(routeKey(next)) || 0);
    write({ route: next, depth: entry.depth + (entry.menu ? 0 : 1), key, menu: false }, entry.menu);
  };
  useEffect(() => {
    const button = window.Telegram?.WebApp?.BackButton;
    if (!window.Telegram?.WebApp?.initData || !button) return;
    const onBack = () => { if (requestLayerBack()) return; if (entry.depth > 0) history.back(); else if (entry.route.bagId) closeLayer(); };
    if (hasBackLayer() || entry.depth > 0 || entry.route.bagId) button.show?.();
    else button.hide?.();
    button.onClick?.(onBack);
    return () => { button.offClick?.(onBack); button.hide?.(); };
  }, [entry.depth, entry.menu, entry.route.bagId, entry.route.gallery, layerVersion]);
  return { ...entry, go, goHome, back, openMenu, closeMenu, openBag, closeLayer };
}
